import Lang from 'lang.js'
import baseMoment from 'moment'
import 'moment/dist/locale/cs'
import {RuntimeConfig} from 'nuxt/schema'
import {useSubjectStore} from '~/stores/subject'

export default defineNuxtPlugin(async() => {
  const config: RuntimeConfig = useRuntimeConfig()
  const translationsStore = useTranslationsStore()

  if (
    !translationsStore.getTranslations ||
    (Array.isArray(translationsStore.getTranslations) && translationsStore.getTranslations.length === 0)
  ) {
    await translationsStore.fetchTranslations()
  } else {
    translationsStore.fetchTranslations()
  }

  const locale = (): string => {
    let fallbackLang = navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language

    if (!['cs', 'sk'].includes(fallbackLang)) {
      fallbackLang = 'cs'
    }

    if (config.public.APP_ENV === 'demo') {
      return fallbackLang
    }

    const userStore = useUserStore()

    return userStore.user?.lang ?? fallbackLang
  }

  const normalizeMomentStringInput = (input: string): string => {
    const trimmedInput = input.trim()

    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/.test(trimmedInput)) {
      return trimmedInput.replace(' ', 'T')
    }

    return trimmedInput
  }

  const moment = (input?: baseMoment.MomentInput): baseMoment.Moment => {
    baseMoment.locale(locale())

    if (typeof input === 'string') {
      return baseMoment(normalizeMomentStringInput(input))
    }

    return baseMoment(input)
  }

  const addDays = (days: number) => {
    const date = new Date()

    date.setDate(date.getDate() + days)

    return date
  }

  const formatDateTime = (ISOString: string, locale: string = 'cs-CZ') => {
    if (ISOString && ISOString.toString().slice(-1) !== 'Z') ISOString += 'Z'
    return ISOString ? new Date(ISOString).toLocaleString(locale) : ''
  }

  const formatDate = (ISOString: string, locale: string = 'cs-CZ') => {
    if (ISOString && ISOString.toString().slice(-1) !== 'Z') ISOString += 'Z'
    return ISOString ? new Date(ISOString).toLocaleDateString(locale) : ''
  }

  const formatNumber = (number: number, locale = 'cs-CZ', decimals = 2): string => {
    // Use a formatter that forces two decimal places
    // and separates thousands by czech standard
    const numberFormatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    const formattedNumber = numberFormatter.format(isNaN(Number(number)) ? 0 : number)

    return formattedNumber
  }

  function formatPercentage(number) {
    if (isNaN(Number(number))) {
      return 0
    }
    if (number < 0.01) {
      return (number * 1000).toFixed(1) + '‰'
    } else {
      return Math.round((number * 100)) + '%'
    }
  }

  interface Address {
    conscriptionNumber?: string
    provisionalNumber?: string
    streetNumber?: string
    city?: string
    street?: string
  }

  const getVolumePrefix = (isWasteCollectionYardWaste: boolean) => {
    //isWasteCollectionYardWaste se vezme vždy z itemu
    const subject = useSubjectStore()
    const units = ['kg', 'l']
    if (isWasteCollectionYardWaste) {
      return units[0]
    }
    if (subject.motivationType) {
      //do subject.motivationType se ukládá useVolumeAsUnits z motivation-info které se fetchuje
      return units[1]
    }
    return units[0]
  }
  const formatStreetAddress = (address: Address): string => {
    const conscriptionNumber = address?.conscriptionNumber
    const provisionalNumber = address?.provisionalNumber
    const streetNumber = address?.streetNumber
    const city = address?.city
    let houseNumber: string | null = null

    if (conscriptionNumber) {
      if (streetNumber) {
        if (provisionalNumber) houseNumber = `${conscriptionNumber}/${streetNumber} ${provisionalNumber}`
        else houseNumber = `${conscriptionNumber}/${streetNumber}`
      } else houseNumber = conscriptionNumber
    } else if (streetNumber) houseNumber = streetNumber
    if (houseNumber && city) {
      houseNumber += ' ' + city
    } else if (!houseNumber && city) houseNumber = city
    return houseNumber ? `${address?.street ?? address?.city ?? ''} ${houseNumber}`.trimStart() : address?.street ?? ''
  }

  const getLocaleString = () => {
    const langToLocale = {cs: 'cs-CZ', sk: 'sk-SK'}

    return langToLocale[locale()] ?? 'sk-SK'
  }

  const formatCurrency = (amount: number, maximumFractionDigits = 2): string => {
    const langToCurrency = {cs: 'CZK', sk: 'EUR'}

    const currencyFormatter = new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      maximumFractionDigits: maximumFractionDigits,
      currency: langToCurrency[locale()] ?? 'EUR',
    })
    if (!amount || isNaN(parseInt(amount))) {
      amount = 0
    }
    return currencyFormatter.format(amount)
  }

  const trans = new Lang({
    messages: translationsStore.getTranslations || {},
    locale: locale(),
    fallback: 'cs',
  })

  const _ = (key: string, parameters = null, count = null, defaultValue = null): string => {
    trans.setLocale(locale())

    const slashPosition = key.indexOf('/')

    if (slashPosition > -1) {
      key = key.substring(slashPosition + 1)
    }

    if (trans.has(key)) {
      if (count) {
        return trans.choice(key, count)
      }

      return trans.get(key, parameters)
    }

    return defaultValue ? defaultValue : key
  }

  const prefixImageUrl = (path: string) => {
    return `${'https://api-ts.jrkdigital.eu'}/${path}`
  }
  const prefixImageUrlObec = (path: string) => {
    return `${'https://api.jrkdigital.eu'}/${path}`
  }
  const getYearsForSelect = () => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = currentYear; i >= currentYear - 10; i--) {
      years.push(i)
    }
    return years
  }

  return {
    provide: {
      _,
      locale,
      addDays,
      formatNumber,
      formatCurrency,
      getLocaleString,
      moment,
      getVolumePrefix,
      prefixImageUrl,
      formatStreetAddress,
      prefixImageUrlObec,
      formatPercentage,
      formatDateTime,
      formatDate,
      getYearsForSelect
    },
  }
})
